import Cookies from 'js-cookie';
import MicroModal from 'micromodal';

const {registerModule} = window;

class Modal {
  constructor(element) {
    this.element = element;
    this.initialize();
    // Set modal to be ready after initalization is complete. This can be detected from outside the class to determine if this modal is ready to be interacted with.
    this.modalIsReady = true;
    // Once all the setup is done dispatch an event to the document so other modules know the modal is ready
    this.dispatchModalReady();
  }

  dispatchModalReady() {
    // Adds a new event to document that fires when the modal is ready and can be listened to with addEventListener('everlaw-modal-ready', ...)
    document.dispatchEvent(new CustomEvent('everlaw-modal-ready', {detail: {modal: this}}));
  }

  bindUI() {
    this.ui = {};
    this.ui.$modal = this.element.querySelector('.modal');
  }

  isModalOpen() {
    // Micromodal
    return this.ui.$modal.classList.contains(this.openClassName);
  }

  setUrlHashVisible(visible) {
    if (visible) {
      // Modify the URL to include the #hash without changing history
      return history.replaceState(null, '', this.modalIdWithLeadingHash);
    }
    // Modify the URL to remove the modal's ID (ex: #hash), but make sure to keep query params in the URL.
    return history.replaceState(null, '', location.pathname + location.search);
  }


  showModal() {
    // Modal must be closed (this helps prevent errors if showModal is called multiple times), but don't show the modal if there's a cookie set
    if (!this.isModalOpen() && !this.modalHasExistingCookie()) {
      // Config needs to be passed to show like this or won't apply
      MicroModal.show(this.microModalTargetId, {
        awaitCloseAnimation: true,
        // Prevent the user from scrolling on the page while the page is loaded
        disableScroll: true,
        // Don't autofocus on the first elements (fixes a bug where buttons will show their hover focus state creating different styles)
        disableFocus: true,
        openClass: this.openClassName,
        onShow: (modal) => {
          if (this.enableUrlTrigger) {
            this.setUrlHashVisible(true);
          }
          // Don't set a cookie if cookie mode is disabled
          if (this.areCookiesEnabled()) {
            // Cookie is set after the modal is shown to allow it to show once
            this.setModalCookieIfUnset();
          }
          // onModalOpen can be provided to execute additional code on open, designed mainly for use for non-modal modules, but may be null if not defined
          if (this.onModalOpen) {
            this.onModalOpen(modal);
          }
        },
        onClose: (modal) => {
          if (this.enableUrlTrigger) {
            this.setUrlHashVisible(false);
          }
          // onModalClose can be provided to execute additional code on open, designed mainly for use for non-modal modules, but may be null if not defined
          if (this.onModalClose) {
            this.onModalClose(modal);
          }

          const iframe = modal.querySelector('.lite-video--vimeo iframe');
          const command = {
            method: 'pause',
            value: 'true',
          };
          if (iframe) {
            iframe.contentWindow.postMessage(JSON.stringify(command), '*');
          }
        },
      });
    }
  }

  closeModal() {
    // Only close the modal if it's open in case it's programmatically triggered multiple times.
    if (this.isModalOpen()) {
      MicroModal.close(this.microModalTargetId);
    }
  }

  hasModalUrlHash() {
    return window.location.hash === this.modalIdWithLeadingHash;
  }

  modalHasExistingCookie() {
    // Return if cookie modal is enabled and there is an existing cookie
    return this.areCookiesEnabled() && Cookies.get(this.modalId) === 'disabled';
  }

  setModalCookieIfUnset() {
    // Get the cookie for this modal, if any.
    const cookiedModal = Cookies.get(this.modalId);

    // All cookies are constrained to the current path of the page see js-cookie docs for info. Cookies default to session in this case which means it expires when the page closes.
    const settings = {path: ''};
    if (this.cookieDelayDays) {
      // If there's a cookie delay then add an expiry this changes the mode from session to expire after a certain number of days.
      settings['expires'] = this.cookieDelayDays;
    }
    if (cookiedModal !== 'disabled') {
      // If this cookie is not set then set it. It's set to the string 'disabled' to for readability.
      Cookies.set(this.modalId, 'disabled', settings);
    }
  }

  hijackLinksToShowModal() {
    // If an empty string is passed as the trigger ID (which isn't a valid trigger) this will target elements with just # which picks up the wrong links. Make sure there's at least two characters to prevent the selector below from targeting the incorrect links.
    if (this.modalIdWithLeadingHash.length <= 1) {
      return;
    }
    // Select all links that match the 'href' attribute attribute and prevent them from functioning. This is necessary because links add elements to history which gets to user stuck in a modal loop when browsing and going back.
    document.querySelectorAll(`a[href="${this.modalIdWithLeadingHash}"]`).forEach((element) => {
      element.addEventListener('click', (event) => {
        this.showModal();
        event.preventDefault();
      });
    });
  }

  listenForExitIntent() {
    // If the modal isn't cookied then set an event listener to open it when the user mouses out of the page
    if (!this.modalHasExistingCookie()) {
      document.addEventListener('mouseout', (e) => {
        if (!e.relatedTarget && !this.exitIntentTriggerFired) {
          this.showModal();

          // Prevent the modal from being continuously fired when the exit intent conditions are met (otherwise modal will appear everytime the user scrolls out of the page)
          this.exitIntentTriggerFired = true;

          this.setModalCookieIfUnset();
        }
      });
    }
  }

  listenForPercentOfPageViewedTrigger() {
    document.addEventListener('scroll', () => {
      // Calculate what percentage of the page is scrolled
      const percent = (document.documentElement.scrollTop || document.body.scrollTop) / ((document.documentElement.scrollHeight || document.body.scrollHeight) - document.documentElement.clientHeight) * 100;
      if (percent >= this.triggerAfterPercentOfPageViewed && !this.percentOfPageViewTriggerHasFired) {
        this.showModal();

        // Prevent the modal from being continuously fired once the user passes the trigger percentage
        this.percentOfPageViewTriggerHasFired = true;

        // Set a cookie to prevent the modal from retriggering if the modal is in a cookie-enabled mode
        this.setModalCookieIfUnset();
      }
    });
  }

  initialize() {
    this.bindUI();
    this.setVars();

    // The 'this' value is messed up within event listeners and the class method so we bind the correct value so that this.canDoThis()
    const hasModalUrlHash = this.hasModalUrlHash.bind(this);
    const showModal = this.showModal.bind(this);
    const hijackLinksToShowModal = this.hijackLinksToShowModal.bind(this);

    // Even though this requires the DOM to be ready _src/static/js/lib/register-module.js waits until the DOM is loaded to there's no need to add a listener for that.
    hijackLinksToShowModal();

    if (this.triggerOnExitIntent) {
      // Delay before activating the exit intent - designed to protect modals from opening if users scrolling in/out of the page when first navigating to it
      setTimeout(() => this.listenForExitIntent(), this.exitIntentActivationDelay);
    }

    if (this.triggerAfterPercentOfPageViewed) {
      this.listenForPercentOfPageViewedTrigger();
    }

    if (this.enableUrlTrigger) {
      window.addEventListener('load', () => {
        // Show modal if the #trigger is present on page load
        if (hasModalUrlHash()) {
          showModal();
        }
      });
      window.addEventListener('hashchange', () => {
        // Show modal when the #trigger is added or removed
        if (hasModalUrlHash()) {
          showModal();
        }
      });
    }
  }

  areCookiesEnabled() {
    // Check if one of the two cookie modes is enabled
    return this.enableCookieSessionMode || this.enableCookieDelayRetriggerMode;
  }

  getCookieDelayDays() {
    // Parses the data attribute and returns the number of days (necessary because data attributes are strings).
    const cookieDelayDays = parseInt(this.element.dataset.enableCookieDelayRetriggerDays);
    if (!isNaN(cookieDelayDays) && cookieDelayDays > 0) {
      return cookieDelayDays;
    }
  }

  getExitIntentActivationDelay() {
    const exitIntentActivationDelaySeconds = parseFloat(this.element.dataset.exitIntentActivationDelay);
    if (isNaN(exitIntentActivationDelaySeconds)) {
      return this.defaultExitIntentActivationDelay;
    }
    return exitIntentActivationDelaySeconds * 1000;
  }

  getTriggerAfterPercentOfPageViewed() {
    // Convert the data attribute to a number containing the % of page viewed.
    const triggerAfterPercentOfPageViewed = parseInt(this.element.dataset.triggerAfterPercentOfPageViewed);
    if (!isNaN(triggerAfterPercentOfPageViewed)) {
      return triggerAfterPercentOfPageViewed;
    }
  }

  setVars() {
    // Define variables
    this.modalId = this.element.dataset.modalId;
    this.enableUrlTrigger = this.element.hasAttribute('data-enable-url-trigger');
    this.enableCookieSessionMode = this.element.hasAttribute('data-enable-cookie-session');
    this.enableCookieDelayRetriggerMode = this.element.hasAttribute('data-enable-cookie-delay-retrigger-days');
    this.onModalOpen = null;
    this.onModalClose = null;
    this.microModalTargetId = `${this.modalId}-id`;
    this.cookieDelayDays = this.getCookieDelayDays();
    this.modalIdWithLeadingHash = `#${this.modalId}`;
    this.openClassName = 'is-open';
    this.triggerOnExitIntent = this.element.hasAttribute('data-trigger-on-exit-intent');
    this.triggerAfterPercentOfPageViewed = this.getTriggerAfterPercentOfPageViewed();
    this.defaultExitIntentActivationDelay = 3000;
    this.exitIntentActivationDelay = this.getExitIntentActivationDelay();
  }
}

registerModule('Modal', Modal);
